import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, languageNames } = useLanguage();

  const servicesLinks = [
    { path: '/services/term-life', label: 'Term Life Insurance' },
    { path: '/services/whole-life', label: 'Whole Life Insurance' },
    { path: '/services/universal-life', label: 'Universal Life Insurance' },
    { path: '/premium-financing', label: 'Premium Financing' },
    { path: '/services/estate-planning', label: 'Estate Planning Guidance' },
  ];

  const advisorLinks = [
    { path: '/business-model', label: 'Business Model' },
    { path: '/training-licensing', label: 'Training & Licensing' },
    { path: '/compensation-model', label: 'Compensation Model' },
    { path: '/income-opportunity', label: 'Income Opportunity' },
    { path: '/success-stories', label: 'Success Stories' },
    { path: '/leadership-model', label: 'Leadership Model' },
    { path: '/technology-platform', label: 'Technology Platform' },
    { path: '/become-an-agent', label: 'Become an Agent' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#020617]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <span className="text-xl font-bold tracking-tight">
              <span className="text-[#C5A059]">Shop</span>
              <span className="text-white">CompareInsure</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                isActive('/') ? 'text-[#C5A059]' : 'text-slate-400 hover:text-[#C5A059]'
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#C5A059] transition-colors flex items-center" data-testid="nav-services-dropdown">
                Services <ChevronDown className="w-3 h-3 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-700 min-w-[200px]">
                <DropdownMenuItem asChild>
                  <Link to="/services" className="text-slate-300 hover:text-white focus:text-white cursor-pointer">
                    All Services
                  </Link>
                </DropdownMenuItem>
                {servicesLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link to={link.path} className="text-slate-300 hover:text-white focus:text-white cursor-pointer">
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* For Advisors Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#C5A059] transition-colors flex items-center" data-testid="nav-advisors-dropdown">
                For Advisors <ChevronDown className="w-3 h-3 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-700 min-w-[200px]">
                <DropdownMenuItem asChild>
                  <Link to="/for-advisors" className="text-slate-300 hover:text-white focus:text-white cursor-pointer">
                    Overview
                  </Link>
                </DropdownMenuItem>
                {advisorLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link to={link.path} className="text-slate-300 hover:text-white focus:text-white cursor-pointer">
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/about"
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                isActive('/about') ? 'text-[#C5A059]' : 'text-slate-400 hover:text-[#C5A059]'
              }`}
            >
              About
            </Link>

            <Link
              to="/contact"
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                isActive('/contact') ? 'text-[#C5A059]' : 'text-slate-400 hover:text-[#C5A059]'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Right Side - Language & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[120px] bg-transparent border-slate-700 text-slate-300 text-xs" data-testid="language-selector">
                <Globe className="w-4 h-4 mr-2 text-[#C5A059]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {Object.entries(languageNames).map(([code, name]) => (
                  <SelectItem key={code} value={code} className="text-slate-300 hover:text-white focus:bg-slate-800">
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Link
              to="/compare"
              data-testid="nav-cta-button"
              className="bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-5 py-2.5 uppercase tracking-wider text-xs transition-all duration-300"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-slate-300 hover:text-[#C5A059] transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-800 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col space-y-1">
              <Link to="/" onClick={() => setIsOpen(false)} className="px-4 py-3 text-slate-300 hover:text-[#C5A059]">Home</Link>
              
              <div className="px-4 py-2 text-xs text-slate-500 uppercase tracking-wider">Services</div>
              <Link to="/services" onClick={() => setIsOpen(false)} className="px-6 py-2 text-sm text-slate-400 hover:text-[#C5A059]">All Services</Link>
              {servicesLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className="px-6 py-2 text-sm text-slate-400 hover:text-[#C5A059]">{link.label}</Link>
              ))}
              
              <div className="px-4 py-2 text-xs text-slate-500 uppercase tracking-wider mt-2">For Advisors</div>
              <Link to="/for-advisors" onClick={() => setIsOpen(false)} className="px-6 py-2 text-sm text-slate-400 hover:text-[#C5A059]">Overview</Link>
              {advisorLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className="px-6 py-2 text-sm text-slate-400 hover:text-[#C5A059]">{link.label}</Link>
              ))}
              
              <Link to="/about" onClick={() => setIsOpen(false)} className="px-4 py-3 text-slate-300 hover:text-[#C5A059]">About</Link>
              <Link to="/contact" onClick={() => setIsOpen(false)} className="px-4 py-3 text-slate-300 hover:text-[#C5A059]">Contact</Link>
              
              <div className="px-4 py-3">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-full bg-transparent border-slate-700 text-slate-300">
                    <Globe className="w-4 h-4 mr-2 text-[#C5A059]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {Object.entries(languageNames).map(([code, name]) => (
                      <SelectItem key={code} value={code} className="text-slate-300">{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Link to="/compare" onClick={() => setIsOpen(false)} className="mx-4 mt-2 bg-[#C5A059] text-[#020617] font-semibold px-6 py-3 uppercase tracking-wider text-sm text-center">
                Get Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
